import { useState, useEffect, useCallback, useMemo } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import { Button, Grid, Typography } from "@mui/material";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import DefaultPageWrapper from "../../components/wrapper/DefaultPageWrapper";
import { useAsync, useAsyncFn } from "../../hooks/useAsync";
import NavigationDrawer from "../../components/drawer/NavigationDrawer";
import useDialog from "../../hooks/useDialog";
import { NAVIGATION_DRAWER_TYPE_OFFERS } from "../../components/drawer/NavigationDrawer/NavigationDrawer";
import CreateItemDialog from "../../components/dialog/CreateItemDialog";
import ControlPointOutlinedIcon from "@mui/icons-material/ControlPointOutlined";
import { centerVericalAlignIconStyle } from "../../helpers/styles";
import BaseBox from "../../components/base/BaseBox/baseBox";
import PropTypes from "prop-types";
import { useForm } from "../../hooks/useForm";
import AutocompleteField from "../../components/field/AutocompleteField";
import useOfferService from "../../services/offerService";
import useItemService from "../../services/itemService";
import { useSnackbarAlert } from "../../context/snackbarAlert";
import { getErrorMsg, isEmptyValue } from "../../helpers/methods";
import BoxWithTypography from "../../components/box/BoxWithTypography/BoxWithTypography";
import ItemRecord from "../../components/other/ItemRecord/ItemRecord";
import ModeEditOutlineIcon from '@mui/icons-material/ModeEditOutline';
import SubcontractorDetailsDialog from "../../components/dialog/SubcontractorDetailsDialog/SubcontractorDetailsDialog";
import LoadingButton from "@mui/lab/LoadingButton";
import useItemFormValidation from "../../hooks/useItemFormValidation";
import useLocationService from "../../services/locationService";
import SelectItemDialog from "../../components/dialog/SelectItemDialog";
import { MANAGE_SERVICES_PERMISSION, SERVICE_MAIN_BRANCH_NAME } from "../../helpers/constants";

import useCheckPermission from "../../hooks/usePermission";
import useDialogWithId from "../../hooks/useDialogWithId"; import useFilterSearchParams from "../../hooks/useFilterSearchParams";
import { SERVICE_REGISTRY_PREFIX, SERVICE_REGISTRY_TABLE_FIELDS_CONFIG, SERVICE_REGISTRY_TABLE_NAME } from "./PageTablesConfig";
import TableService from "../../services/tableService";
import UniversalToolBarWithDialogs from "../../components/bar/UniversalToolBarWithDialogs";
import FilterTable from "../../components/table/FilterTable";
import usePaginationWithSearchParams from "../../hooks/usePaginationWithSearchParams";
import useLocalData from "../../hooks/useLocalData";
import { serviceExportXslxUrl } from "../../helpers/apiUrls";
import { useLocation } from "react-router-dom";
;

const TABLE_CONFIGS = [
  { name: SERVICE_REGISTRY_TABLE_NAME, config: SERVICE_REGISTRY_TABLE_FIELDS_CONFIG },
];

export default function ServiceRegistryPage(props) {
  const { pageName } = props;


  const { t } = useTranslation();
  const [hasPermissionToManage] = useCheckPermission(MANAGE_SERVICES_PERMISSION)
  const [isCopyMode, setCopyMode] = useState(false)


  const [hiddenColumnsForTables, setHiddenColumnsForTables] = useState([]);
  const [columnsOrdersForTables, setColumnsOrdersForTables] = useState([]);
  const [serviceSearchParams, setServiceSearchParams] = useState({})


  const [
    openItemDialog,
    selectedServiceId,
    onOpenItemDialog,
    onCloseItemDialog,
  ] = useDialogWithId();


  const {
    page: servicePage,
    pageSize: servicePageSize,
    handleChangePageWithSearchParams: handleChangeServicePage,
    handleChangePageSizeWithSearchParams: handleChangeServicePageSize,
    resetPageNumber,
    searchParams,
    setSearchParams
  } = usePaginationWithSearchParams(SERVICE_REGISTRY_PREFIX, { 'page_size': 100 }, 100);


  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page_size', servicePageSize || 100);
    setSearchParams(newSearchParams)
  }, [searchParams, props.itemType]);

  const {
    setNewItemSearchParamsIfAreChanged,
  } = useFilterSearchParams(searchParams);

  useEffect(() => {
    setNewItemSearchParamsIfAreChanged(
      SERVICE_REGISTRY_PREFIX,
      serviceSearchParams,
      setServiceSearchParams
    );
  }, [searchParams]);

  const {
    getServiceListData,
    getServiceFilteringData } = useOfferService();


  const {
    data,
    dataCount: countRecords,
    dataRefetch
  } = useLocalData(getServiceListData, serviceSearchParams, true)

  const filteringData = useAsync(getServiceFilteringData)

  const onClickEdit = useCallback((e, itemId) => {
    e.stopPropagation();
    onOpenItemDialog(itemId)
  }, [])

  const onClickCopy = useCallback((e, itemId) => {
    e.stopPropagation();
    setCopyMode(true)
    onOpenItemDialog(itemId)

  }, [])

  const itemsData = React.useMemo(
    () =>
      TableService.getPreparedDataForCollapsibleTable(
        SERVICE_REGISTRY_TABLE_FIELDS_CONFIG,
        data ? data : [],
        hiddenColumnsForTables ? hiddenColumnsForTables[SERVICE_REGISTRY_TABLE_NAME] : [],
        columnsOrdersForTables ? columnsOrdersForTables[SERVICE_REGISTRY_TABLE_NAME] : [],
        onClickEdit,
        onClickCopy,
        hasPermissionToManage
      ),
    [SERVICE_REGISTRY_TABLE_FIELDS_CONFIG,
      data,
      hiddenColumnsForTables,
      columnsOrdersForTables,
      onClickEdit,
      onClickCopy,
      hasPermissionToManage]
  );

  const itemsExportHeaders = useMemo(() => {
    return TableService.getTableExportHeaders(
      SERVICE_REGISTRY_TABLE_FIELDS_CONFIG,
      hiddenColumnsForTables ? hiddenColumnsForTables[SERVICE_REGISTRY_TABLE_NAME] : [],
      columnsOrdersForTables ? columnsOrdersForTables[SERVICE_REGISTRY_TABLE_NAME] : []
    );
  }, [
    SERVICE_REGISTRY_TABLE_FIELDS_CONFIG,
    hiddenColumnsForTables,
    columnsOrdersForTables
  ]);

  const onClear = useCallback(() => {
    setSearchParams({})
  }, [])

  const handleCloseItemDialog = useCallback(() => {
    onCloseItemDialog()
    setCopyMode(false)
    dataRefetch()
  }, [])


  const isLoading = data === undefined || filteringData.loading


  const getDrawerType = () => {
    if (searchParams.has('drawer_type')) {
      return searchParams.get('drawer_type')
    }
    return NAVIGATION_DRAWER_TYPE_OFFERS
  }

  return (
    <NavigationDrawer
      pageName={pageName}
      drawerType={getDrawerType()}>
      <DefaultPageWrapper titleKey={"service_registry"}>
        <Grid item xs={12} marginY={1}>
          <UniversalToolBarWithDialogs
            pageName={pageName}
            tableConfig={TABLE_CONFIGS}
            filteringData={filteringData ? filteringData : []}
            refreshTable={dataRefetch}
            resetPageNumber={resetPageNumber}
            hiddenColumnsForTables={hiddenColumnsForTables}
            setHiddenColumnsForTables={setHiddenColumnsForTables}
            columnsOrdersForTables={columnsOrdersForTables}
            setColumnsOrdersForTables={setColumnsOrdersForTables}
            showMassActionButton={false}
            showOpenCloseFilterButton={false}
            showCleanfiltersButton={true}
            showMyFiltersButton={true}
            showSearchButton={true}
            onClickCreateItem={(e) => onOpenItemDialog()}
            itemType={"service"}
            cleanFiltersFn={onClear}
            showCreateButton={true}
            mdGridButton={2.75}

          />
        </Grid>
        <LoaderWrapper showLoader={isLoading}>
          <Grid item xs={12}>
            <FilterTable
              data={itemsData}
              onClickRow={() => undefined}
              countRecords={countRecords}
              resetPageNumber={resetPageNumber}
              page={servicePage}
              onPageChange={handleChangeServicePage}
              rowsPerPage={servicePageSize}
              onRowsPerPageChange={handleChangeServicePageSize}
              style={{ maxHeight: "70vh" }}
              showCleanFilterIcon={true}
              showContextMenu={false}
              showExportToFileButton={true}
              exportToFileUrl={serviceExportXslxUrl}
              exportToFileSearchParams={serviceSearchParams}
              exportToFileHeaders={itemsExportHeaders}
              exportToFileFileName={`${t("tabs.service_registry_tab.services").replace(" ", "_")}.xlsx`}
              headersConfig={TableService.getHeadersConfigForCollapsibleTable(
                SERVICE_REGISTRY_TABLE_FIELDS_CONFIG,
                filteringData ? filteringData : [],
                hiddenColumnsForTables ? hiddenColumnsForTables[SERVICE_REGISTRY_TABLE_NAME] : [],
                columnsOrdersForTables ? columnsOrdersForTables[SERVICE_REGISTRY_TABLE_NAME] : []
              )}
              showDetailsIcon={false}
              showCounterRecords={false}
              showCheckbox={false}
              filterPrefix={SERVICE_REGISTRY_PREFIX}
            />
          </Grid>
        </LoaderWrapper>

        {openItemDialog && (
          <CreateItemDialog
            open={openItemDialog}
            onClose={handleCloseItemDialog}
            onRefetchData={dataRefetch}
            itemType={'service'}
            treeIndex={2}
            itemId={selectedServiceId}
            readOnly={!hasPermissionToManage}
            isCopyObject={isCopyMode}
          />
        )}

      </DefaultPageWrapper>
    </NavigationDrawer>
  );
}
ServiceRegistryPage.propTypes = {
  style: PropTypes.object,
};

ServiceRegistryPage.defaultProps = {

};


